// ============================================================================
// Supplier admin API client — BOM Plan III-A (Task 4)
//
// Admin CRUD endpoints shipped in Tasks 1-3:
//   - GET    /api/suppliers/admin?q=&page=&pageSize=   (0-based paging)
//   - GET    /api/suppliers/{id}
//   - POST   /api/suppliers                            ({name,code,publicUrl})
//   - PATCH  /api/suppliers/{id}                       (If-Match RowVersion)
//   - DELETE /api/suppliers/{id}                       (If-Match; 422 if in use)
//
// Mirrors `productsAdminApi` (web/src/lib/products-admin.ts). NOTE: the
// `/admin` endpoint is 0-based — `listAdmin` defaults to `page=0`.
// ============================================================================

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

export interface SupplierAdminRow { supplierId: number; name: string; code: string | null; publicUrl: string | null; legacyManufacturerId: number | null; rowVersionBase64: string | null; }
export interface SupplierAdminPage { items: SupplierAdminRow[]; total: number; page: number; pageSize: number; }
export interface CreateSupplierBody { name: string; code: string | null; publicUrl: string | null; }
export type UpdateSupplierBody = CreateSupplierBody;

export const suppliersAdminApi = {
  listAdmin: (q: string, page = 0, pageSize = 50): Promise<SupplierAdminPage> =>
    apiFetch<SupplierAdminPage>(`/api/suppliers/admin?page=${page}&pageSize=${pageSize}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
  get: (id: number): Promise<SupplierAdminRow> =>
    apiFetch<SupplierAdminRow>(`/api/suppliers/${id}`),
  create: (body: CreateSupplierBody): Promise<SupplierAdminRow> =>
    apiFetch<SupplierAdminRow>("/api/suppliers", { method: "POST", body: JSON.stringify(body) }),
  update: (id: number, body: UpdateSupplierBody, rowVersionBase64: string | null): Promise<SupplierAdminRow> =>
    apiFetch<SupplierAdminRow>(`/api/suppliers/${id}`, { method: "PATCH", body: JSON.stringify(body), headers: rowVersionBase64 ? { "If-Match": rowVersionBase64 } : {} }),
  remove: (id: number, rowVersionBase64: string | null): Promise<void> =>
    apiFetch<void>(`/api/suppliers/${id}`, { method: "DELETE", headers: rowVersionBase64 ? { "If-Match": rowVersionBase64 } : {} }),
};

export const supplierKeys = {
  adminList: (q: string, page: number) => ["suppliers", "admin", page, q] as const,
};

/**
 * Fetch a page of suppliers for the admin list. 0-based paging.
 */
export function useSuppliersAdmin(q: string, page = 0) {
  return useQuery({
    queryKey: supplierKeys.adminList(q, page),
    queryFn: () => suppliersAdminApi.listAdmin(q, page),
  });
}
